<template>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- NPC / Header -->
    <div class="mb-6">
      <div class="relative bg-gray-700 bg-opacity-70 p-6 rounded-xl shadow-xl flex items-center space-x-6 transition-all duration-300 hover:shadow-indigo-500/30">
        <div class="relative">
          <img
            :src="managerImage"
            alt="ผู้จัดการคอนโด"
            @error="handleImageError"
            class="w-20 h-20 rounded-xl object-cover border-2 border-indigo-400 shadow-md transition-all duration-200 hover:border-indigo-500"
          />
          <div class="absolute -bottom-2 -right-2 bg-indigo-600 bg-opacity-90 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-md">
            ผู้จัดการ
          </div>
        </div>

        <div class="flex-1">
          <h2 class="text-xl font-semibold text-indigo-300 tracking-wide">ผู้จัดการตึก</h2>
          <p class="text-indigo-200 italic text-sm mt-1">{{ randomMessage }}</p>

          <!-- ปุ่มประกันและย้ายตึก -->
          <div class="mt-4 flex flex-wrap gap-2">
            <button
              @click="insuranceOpen = true"
              class="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white text-sm font-semibold rounded-md shadow-lg transition-all duration-200"
            >
              <i class="fas fa-shield-alt mr-2 text-yellow-300"></i>
              ซื้อประกันตัวละคร
            </button>
            <button
              @click="openMoveBuildingModal"
              :disabled="!canMoveBuilding || !canMoveBuildingByRoom"
              class="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white text-sm font-semibold rounded-md shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fas fa-building mr-2 text-blue-300"></i>
              ย้ายตึก
              <span v-if="!canMoveBuilding" class="ml-2 text-xs">({{ timeUntilNextMove }})</span>
              <span v-else-if="!canMoveBuildingByRoom" class="ml-2 text-xs">(ต้อง 12 ห้อง)</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- รายการห้องแบบการ์ด -->
    <div class="container mx-auto">
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <div
          v-for="(room, index) in roomRequirements"
          :key="index"
          :class="getCardClass(index)"
          @click="openModal(index)"
          class="relative group bg-gray-700 rounded-lg p-3 shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
        >
          <!-- หน้าประตู -->
          <div class="relative h-28 w-full mb-3 overflow-hidden rounded-t-lg">
            <div
              :class="{
                'bg-gradient-to-b from-gray-800 to-gray-900': playerData.room <= index,
                'bg-gradient-to-b from-green-900 to-gray-800': playerData.room > index
              }"
              class="absolute inset-0"
            ></div>
            <i
              :class="{
                'fa-solid fa-door-closed text-red-400': playerData.room <= index,
                'fa-solid fa-door-open text-green-400': playerData.room > index
              }"
              class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-4xl"
            ></i>
            <div v-if="playerData.room === index + 1 && canUnlockRoom(index)" class="absolute inset-0 animate-pulse bg-green-500 bg-opacity-20"></div>
            <div v-if="playerData.room <= index" class="absolute top-2 right-2 text-red-400 text-sm">
              <i class="fas fa-lock"></i>
            </div>
          </div>

          <!-- ข้อมูลห้อง -->
          <h4 class="text-base font-semibold text-indigo-300 text-center mb-2">ห้อง {{ index + 1 }}</h4>
          <div class="text-xs text-indigo-200 space-y-1">
            <p v-if="playerData.room > index" class="text-green-400 text-center">
              <i class="fas fa-check-circle mr-1"></i> ปลดล็อคแล้ว
            </p>
            <p v-else class="text-red-400 text-center">
              <i class="fas fa-circle-xmark mr-1"></i> ยังไม่ปลดล็อค
            </p>
          </div>

          <!-- ปุ่มลดห้อง: แสดงเฉพาะห้องปัจจุบัน (ไม่ใช่ห้อง 1) -->
          <button
            v-if="playerData.room > index && index > 0 && playerData.room === index + 1"
            @click.stop="openDowngradeModal(index)"
            class="mt-2 w-full bg-red-700 hover:bg-red-600 text-white text-xs py-1 rounded-md transition-colors duration-200"
          >
            <i class="fas fa-arrow-down mr-1"></i> ลดห้อง
          </button>
        </div>
      </div>
    </div>

    <!-- Modal ปลดล็อคห้อง -->
    <div v-if="showModal" class="fixed inset-0 z-10 w-screen overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div class="fixed inset-0 bg-gray-900 bg-opacity-70 transition-opacity duration-300"></div>
      <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
        <div class="relative transform overflow-hidden rounded-lg bg-gray-800 text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-md">
          <!-- กำลังปลดล็อค -->
          <div v-if="isUnlocking" class="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-20">
            <div class="animate-spin-pulse rounded-full h-8 w-8 border-t-2 border-indigo-600"></div>
          </div>

          <div class="p-5">
            <!-- แจ้งเตือนคุณสมบัติไม่พอ -->
            <div v-if="insufficientRequirements.length > 0" class="mb-4 bg-red-900/30 border border-red-500 rounded-md p-3 text-sm text-red-300">
              <div class="flex justify-between items-center">
                <p>คุณสมบัติหรือทรัพยากรไม่เพียงพอ:</p>
                <button @click="insufficientRequirements = []" class="text-red-200 hover:text-red-100">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <ul class="list-disc list-inside mt-2 text-xs">
                <li v-for="req in insufficientRequirements" :key="req">{{ req }}</li>
              </ul>
            </div>

            <!-- หัวข้อ -->
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-xl font-semibold text-indigo-300 flex items-center">
                <i class="fas fa-unlock-alt mr-2"></i> ปลดล็อคห้อง {{ selectedRoom + 1 }}
              </h3>
              <button
                @click="closeModal"
                class="text-gray-400 hover:text-indigo-300 transition-colors duration-200"
                :disabled="isUnlocking"
              >
                <i class="fas fa-times text-lg"></i>
              </button>
            </div>

            <!-- รายการเงื่อนไข -->
            <div class="space-y-2 mb-4 max-h-64 overflow-y-auto pr-2">
              <div
                :class="[
                  playerData.room >= roomRequirements[selectedRoom].requiredRoom ? 'border-green-500' : 'border-red-500',
                  'flex items-center justify-between rounded-md p-3 text-sm border-2 bg-gray-700 transition-all duration-200 hover:bg-gray-600'
                ]"
              >
                <span class="text-indigo-200 flex items-center"><i class="fas fa-door-closed mr-2"></i> ห้องขั้นต่ำ</span>
                <span class="text-indigo-300 font-semibold">{{ roomRequirements[selectedRoom].requiredRoom }} (มี: {{ playerData.room }})</span>
              </div>
              <div
                :class="[
                  playerData.level >= roomRequirements[selectedRoom].requiredLevel ? 'border-green-500' : 'border-red-500',
                  'flex items-center justify-between rounded-md p-3 text-sm border-2 bg-gray-700 transition-all duration-200 hover:bg-gray-600'
                ]"
              >
                <span class="text-indigo-200 flex items-center"><i class="fas fa-star mr-2"></i> เลเวลขั้นต่ำ</span>
                <span class="text-indigo-300 font-semibold">{{ roomRequirements[selectedRoom].requiredLevel }} (มี: {{ playerData.level }})</span>
              </div>
              <div
                :class="[
                  playerData.renown >= roomRequirements[selectedRoom].requiredRenown ? 'border-green-500' : 'border-red-500',
                  'flex items-center justify-between rounded-md p-3 text-sm border-2 bg-gray-700 transition-all duration-200 hover:bg-gray-600'
                ]"
              >
                <span class="text-indigo-200 flex items-center"><i class="fas fa-trophy mr-2"></i> ชื่อเสียงขั้นต่ำ</span>
                <span class="text-indigo-300 font-semibold">{{ roomRequirements[selectedRoom].requiredRenown }} (มี: {{ playerData.renown }})</span>
              </div>
              <div
                :class="[
                  playerData.silver >= roomRequirements[selectedRoom].silver ? 'border-green-500' : 'border-red-500',
                  'flex items-center justify-between rounded-md p-3 text-sm border-2 bg-gray-700 transition-all duration-200 hover:bg-gray-600'
                ]"
              >
                <span class="text-indigo-200 flex items-center"><i class="fas fa-coins mr-2 text-gray-100"></i> เหรียญ</span>
                <span class="text-indigo-300 font-semibold">{{ roomRequirements[selectedRoom].silver }} (มี: {{ playerData.silver }})</span>
              </div>
              <div
                :class="[
                  playerData.gold >= roomRequirements[selectedRoom].gold ? 'border-green-500' : 'border-red-500',
                  'flex items-center justify-between rounded-md p-3 text-sm border-2 bg-gray-700 transition-all duration-200 hover:bg-gray-600'
                ]"
              >
                <span class="text-indigo-200 flex items-center"><i class="fas fa-g mr-2 text-yellow-500"></i> ทอง</span>
                <span class="text-indigo-300 font-semibold">{{ roomRequirements[selectedRoom].gold }} (มี: {{ playerData.gold }})</span>
              </div>
              <div
                :class="[
                  playerData.diamond >= roomRequirements[selectedRoom].diamond ? 'border-green-500' : 'border-red-500',
                  'flex items-center justify-between rounded-md p-3 text-sm border-2 bg-gray-700 transition-all duration-200 hover:bg-gray-600'
                ]"
              >
                <span class="text-indigo-200 flex items-center"><i class="fas fa-gem mr-2 text-blue-400"></i> เพชร</span>
                <span class="text-indigo-300 font-semibold">{{ roomRequirements[selectedRoom].diamond }} (มี: {{ playerData.diamond }})</span>
              </div>
            </div>

            <!-- ปุ่มกระทำ -->
            <div class="space-y-2">
              <button
                v-if="playerData.room === selectedRoom && canUnlockRoom(selectedRoom)"
                @click="confirmUnlock"
                :disabled="isUnlocking"
                class="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-md text-sm font-medium transition-colors duration-200 flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="!isUnlocking">ซื้อห้อง</span>
                <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
              </button>
              <button
                @click="closeModal"
                class="w-full bg-gray-600 hover:bg-gray-500 text-white py-2 rounded-md text-sm font-medium transition-colors duration-200"
                :disabled="isUnlocking"
              >
                ยกเลิก
              </button>
            </div>

            <div v-if="errorMessage" class="mt-3 text-center text-red-300 text-xs">
              {{ errorMessage }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal ย้ายตึก -->
    <div v-if="moveBuildingModalOpen" class="fixed inset-0 z-20 w-screen overflow-y-auto" aria-labelledby="move-building-modal-title" role="dialog" aria-modal="true">
      <div class="fixed inset-0 bg-gray-900 bg-opacity-70 transition-opacity duration-300"></div>
      <div class="flex min-h-full items-center justify-center p-3 text-center sm:p-0">
        <div class="relative transform overflow-hidden rounded-xl bg-gray-800 text-left shadow-2xl transition-all sm:my-8 w-full sm:max-w-md flex flex-col" style="max-height: 85vh;">
          <!-- กำลังย้าย -->
          <div v-if="isMovingBuilding" class="absolute inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center z-20 rounded-xl">
            <div class="flex flex-col items-center gap-2">
              <div class="animate-spin-pulse rounded-full h-8 w-8 border-t-2 border-indigo-500"></div>
              <p class="text-indigo-200 text-xs">กำลังย้ายตึก...</p>
            </div>
          </div>

          <!-- Header -->
          <div class="flex justify-between items-center px-4 py-3 border-b border-gray-700 bg-gray-800 flex-shrink-0">
            <h3 class="text-lg font-semibold text-indigo-300 flex items-center">
              <i class="fas fa-building mr-2"></i> ย้ายตึก
            </h3>
            <button
              @click="closeMoveBuildingModal"
              class="text-gray-400 hover:text-indigo-300 transition-colors duration-200 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-700"
              :disabled="isMovingBuilding"
            >
              <i class="fas fa-times text-sm"></i>
            </button>
          </div>

          <!-- Scrollable body -->
          <div class="p-4 overflow-y-auto">
            <!-- Compact requirement bar -->
            <div class="mb-3 bg-gray-900 border border-gray-700 bg-opacity-60 rounded-lg px-3 py-2 flex flex-wrap items-center gap-3 text-xs text-gray-300">
              <span class="inline-flex items-center gap-1">
                <i class="far fa-clock text-blue-400"></i>
                รอ 7 วัน/ครั้ง
              </span>
              <span class="hidden sm:inline text-gray-600">|</span>
              <span class="inline-flex items-center gap-1">
                <i class="fas fa-door-closed text-yellow-400"></i>
                ต้อง 12 ห้อง
              </span>
            </div>

            <!-- Info note -->
            <div class="mb-3 bg-gray-700 border border-gray-600 bg-opacity-50 rounded-lg p-3 text-xs text-gray-300">
              <p class="font-medium text-indigo-200 mb-2">
                <i class="fas fa-info-circle mr-1"></i> เรื่องควรรู้ก่อนย้าย
              </p>
              <ul class="space-y-1 list-disc list-inside text-gray-400">
                <li>ย้ายตึกแล้วประกันปัจจุบันจะหมดอายุ และตัวนับการซื้อประกันจะรีเซ็ตเป็น 0</li>
                <li>ประกันตึก 1: 3 ครั้ง/สัปดาห์ ราคาตามจำนวนห้อง</li>
                <li>ประกันตึก 2: 3 ครั้ง/สัปดาห์ ครั้งที่ 1 จ่าย 300 เพชร ครั้งที่ 2–3 จ่าย 20/30 แคช</li>
              </ul>
            </div>

            <!-- Building selection -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <!-- Building 1 -->
              <div
                @click="selectBuilding(1)"
                :class="[
                  'relative rounded-xl border p-3 cursor-pointer transition-all duration-200',
                  selectedBuilding === 1
                    ? 'bg-indigo-900 border-indigo-500 bg-opacity-20 shadow-lg'
                    : 'bg-gray-700 border-gray-700 bg-opacity-50 hover:bg-gray-700 hover:border-gray-600',
                  playerData.building === 1 ? 'opacity-50 cursor-not-allowed' : ''
                ]"
              >
                <div v-if="selectedBuilding === 1" class="absolute top-2 right-2 text-indigo-400">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div class="flex items-center gap-2 mb-2">
                  <div class="w-8 h-8 rounded-lg bg-indigo-600 bg-opacity-20 flex items-center justify-center text-indigo-300">
                    <i class="fas fa-building"></i>
                  </div>
                  <h4 class="text-sm font-semibold text-indigo-200">ตึก 1</h4>
                </div>
                <p class="text-xs text-gray-400 leading-relaxed">
                  ปิดชื่อในลอบสังหาร<br>
                  โทษตายปกติ
                </p>
                <div v-if="playerData.building === 1" class="mt-2 text-xs text-gray-500 text-center">ตึกปัจจุบัน</div>
              </div>

              <!-- Building 2 -->
              <div
                @click="selectBuilding(2)"
                :class="[
                  'relative rounded-xl border p-3 cursor-pointer transition-all duration-200',
                  selectedBuilding === 2
                    ? 'bg-red-900 border-red-500 bg-opacity-20 shadow-lg'
                    : 'bg-gray-700 border-gray-700 bg-opacity-50 hover:bg-gray-700 hover:border-gray-600',
                  playerData.building === 2 ? 'opacity-50 cursor-not-allowed' : ''
                ]"
              >
                <div v-if="selectedBuilding === 2" class="absolute top-2 right-2 text-red-400">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div class="flex items-center gap-2 mb-2">
                  <div class="w-8 h-8 rounded-lg bg-red-600 bg-opacity-20 flex items-center justify-center text-red-300">
                    <i class="fas fa-skull"></i>
                  </div>
                  <h4 class="text-sm font-semibold text-red-200">ตึก 2</h4>
                </div>
                <p class="text-xs text-gray-400 leading-relaxed">
                  ปิดชื่อเหมือนตึก 1<br>
                  <span class="text-red-300">โทษตายหนัก</span>
                </p>
                <div v-if="playerData.building === 2" class="mt-2 text-xs text-gray-500 text-center">ตึกปัจจุบัน</div>
              </div>
            </div>

            <!-- Building 2 risk warning -->
            <div v-if="selectedBuilding === 2" class="mb-3 bg-red-900 border border-red-500 bg-opacity-20 border-opacity-50 rounded-xl overflow-hidden">
              <details class="group">
                <summary class="flex items-center justify-between cursor-pointer p-3 text-xs text-red-300 select-none">
                  <span class="flex items-center gap-2 font-medium">
                    <i class="fas fa-exclamation-triangle text-red-400"></i>
                    คำเตือนความเสี่ยงตึก 2
                  </span>
                  <i class="fas fa-chevron-down text-gray-500 transition-transform group-open:rotate-180"></i>
                </summary>
                <div class="px-3 pb-3 text-xs text-gray-300 space-y-2">
                  <p class="text-red-300">หากตายในตึก 2 จะสูญเสีย:</p>
                  <div class="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <span>• ไอเทมสวมใส่ <strong class="text-red-300">2-3</strong></span>
                    <span>• สกิล <strong class="text-red-300">2-3</strong></span>
                    <span>• ลายสัก <strong class="text-red-300">2-3</strong></span>
                    <span>• ของในกระเป๋า <strong class="text-red-300">2-3</strong></span>
                    <span>• ห้อง <strong class="text-red-300">0-1</strong></span>
                  </div>
                  <label class="flex items-start gap-2 mt-2 cursor-pointer">
                    <input
                      id="accept-building2-risk"
                      v-model="acceptedBuilding2Risk"
                      type="checkbox"
                      class="mt-0.5 h-4 w-4 text-indigo-600 bg-gray-700 border-gray-500 rounded focus:ring-indigo-500 flex-shrink-0"
                    />
                    <span class="text-gray-300 text-xs">เข้าใจและยอมรับความเสี่ยง หากตายจะสูญเสียทรัพยากรจำนวนมาก</span>
                  </label>
                </div>
              </details>
            </div>

            <!-- Error -->
            <div v-if="errorMessage" class="mb-3 text-center text-red-300 text-xs bg-red-900 border border-red-500 bg-opacity-20 border-opacity-30 rounded-lg py-2 px-3">
              {{ errorMessage }}
            </div>
          </div>

          <!-- Footer actions -->
          <div class="px-4 py-3 border-t border-gray-700 bg-gray-800 flex-shrink-0">
            <div class="flex gap-3">
              <button
                @click="closeMoveBuildingModal"
                class="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                :disabled="isMovingBuilding"
              >
                ยกเลิก
              </button>
              <button
                @click="confirmMoveBuilding"
                :disabled="!canConfirmMoveBuilding"
                class="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white py-2 rounded-lg text-sm font-medium transition-all duration-200 flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="!isMovingBuilding">ยืนยันย้าย</span>
                <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal ยืนยันลดห้อง -->
    <div v-if="showDowngradeModal" class="fixed inset-0 z-10 w-screen overflow-y-auto" role="dialog" aria-modal="true">
      <div class="fixed inset-0 bg-gray-900 bg-opacity-70 transition-opacity duration-300"></div>
      <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
        <div class="relative transform overflow-hidden rounded-lg bg-gray-800 text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-md">
          <div v-if="isDowngrading" class="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-20">
            <div class="animate-spin-pulse rounded-full h-8 w-8 border-t-2 border-red-500"></div>
          </div>
          <div class="p-5">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-xl font-semibold text-red-400 flex items-center">
                <i class="fas fa-arrow-down mr-2"></i> ลดห้อง
              </h3>
              <button @click="closeDowngradeModal" class="text-gray-400 hover:text-red-300 transition-colors duration-200" :disabled="isDowngrading">
                <i class="fas fa-times text-lg"></i>
              </button>
            </div>
            <div class="mb-4 bg-red-900/30 border border-red-500 rounded-md p-4 text-sm text-red-300">
              <p class="font-semibold text-base mb-1">⚠️ คุณแน่ใจหรือไม่?</p>
              <p>ห้องจะลดจาก <span class="font-bold text-white">{{ playerData.room }}</span> → <span class="font-bold text-white">{{ playerData.room - 1 }}</span></p>
              <p class="mt-1 text-xs text-gray-400">การลดห้องไม่มีค่าใช้จ่าย และไม่ได้รับทรัพยากรคืน</p>
            </div>
            <div class="space-y-2">
              <button
                @click="confirmDowngrade"
                :disabled="isDowngrading"
                class="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-md text-sm font-medium transition-colors duration-200 flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="!isDowngrading">ยืนยันลดห้อง</span>
                <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
              </button>
              <button @click="closeDowngradeModal" class="w-full bg-gray-600 hover:bg-gray-500 text-white py-2 rounded-md text-sm font-medium transition-colors duration-200" :disabled="isDowngrading">
                ยกเลิก
              </button>
            </div>
            <div v-if="downgradeError" class="mt-3 text-center text-red-300 text-xs">{{ downgradeError }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Insurance Modal -->
    <div v-if="insuranceOpen" class="fixed inset-0 z-20 flex items-center justify-center bg-gray-900 bg-opacity-70 backdrop-blur-sm">
      <InsuranceModal :is-open="insuranceOpen" @close="insuranceOpen = false" />
    </div>

    <p v-if="errorMessage && !showModal && !moveBuildingModalOpen" class="text-red-300 text-center mt-4 text-sm">{{ errorMessage }}</p>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import InsuranceModal from '@/components/character/InsuranceModal.vue';

export default {
  name: 'ManagerRoom',
  components: { InsuranceModal },
  setup() {
    const { user, isLoadingAuth } = getUser();
    const playerStore = usePlayerStore();

    const Messages = [
      'ยิ่งมีห้องเยอะ คุณยิ่งปลอดภัย',
      'ระวังคนรอบตัวของคุณให้ดีๆ',
      'หวังว่าคุณจะรอดถึงพรุ่งนี้เช้า',
      'ซื้อด่วนอย่าช้า',
    ];

    const randomMessage = ref('');
    const playerData = ref({
      room: 1,
      level: 1,
      renown: 0,
      silver: 0,
      gold: 0,
      diamond: 0,
      building: 1,
      nextBuildingMoveTime: null
    });
    const isFetchingData = ref(true);
    const isUnlocking = ref(false);
    const isMovingBuilding = ref(false);
    const errorMessage = ref('');
    const showModal = ref(false);
    const selectedRoom = ref(null);
    const moveBuildingModalOpen = ref(false);
    const selectedBuilding = ref(null);
    const acceptedBuilding2Risk = ref(false);
    const managerImage = ref('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/criminal%2F10.png?alt=media&token=945364a3-fd57-46e6-8165-30b2efff66d9');
    const insufficientRequirements = ref([]);
    const insuranceOpen = ref(false);

    // Downgrade state
    const showDowngradeModal = ref(false);
    const isDowngrading = ref(false);
    const downgradeError = ref('');



    // กำหนดเงื่อนไขแต่ละห้อง
    const roomRequirements = [
      { requiredRoom: 0, requiredLevel: 3, requiredRenown: 0, silver: 0, gold: 0, diamond: 0 },
      { requiredRoom: 1, requiredLevel: 6, requiredRenown: 0, silver: 200, gold: 0, diamond: 0 },
      { requiredRoom: 2, requiredLevel: 9, requiredRenown: 0, silver: 500, gold: 0, diamond: 0 },
      { requiredRoom: 3, requiredLevel: 12, requiredRenown: 0, silver: 1000, gold: 0, diamond: 0 },
      { requiredRoom: 4, requiredLevel: 15, requiredRenown: 0, silver: 2000, gold: 0, diamond: 0 },
      { requiredRoom: 5, requiredLevel: 15, requiredRenown: 0, silver: 5000, gold: 100, diamond: 100 },
      { requiredRoom: 6, requiredLevel: 15, requiredRenown: 100, silver: 10000, gold: 300, diamond: 300 },
      { requiredRoom: 7, requiredLevel: 15, requiredRenown: 200, silver: 10000, gold: 500, diamond: 500 },
      { requiredRoom: 8, requiredLevel: 15, requiredRenown: 500, silver: 10000, gold: 700, diamond: 700 },
      { requiredRoom: 9, requiredLevel: 15, requiredRenown: 1000, silver: 10000, gold: 1000, diamond: 1000 },
      { requiredRoom: 10, requiredLevel: 15, requiredRenown: 2000, silver: 10000, gold: 1000, diamond: 1000 },
      { requiredRoom: 11, requiredLevel: 15, requiredRenown: 3000, silver: 10000, gold: 1000, diamond: 1000 },
    ];

    // ค่าช่วยแสดงผลจำนวนห้อง
    const totalRooms = computed(() => roomRequirements.length);
    const unlockedRooms = computed(() => {
      return Math.min(playerData.value.room - 1, totalRooms.value);
    });
    const remainingLockedRooms = computed(() => Math.max(totalRooms.value - unlockedRooms.value, 0));
    const progressPercent = computed(() => {
      if (totalRooms.value === 0) return 0;
      return Math.round((unlockedRooms.value / totalRooms.value) * 100);
    });

    // ตรวจสอบว่าสามารถย้ายตึกได้หรือไม่
    const canMoveBuilding = computed(() => {
      const nextMoveTime = toDate(playerData.value.nextBuildingMoveTime);
      if (!nextMoveTime) return true;
      const now = new Date();
      return now >= nextMoveTime;
    });

    // ตรวจสอบว่าห้องครบ 12 ห้องหรือไม่
    const canMoveBuildingByRoom = computed(() => playerData.value.room === 12);

    // ตรวจสอบว่าสามารถกดยืนยันย้ายตึกได้หรือไม่ (รวม checkbox ยอมรับความเสี่ยงตึก 2)
    const canConfirmMoveBuilding = computed(() => {
      if (!selectedBuilding.value || selectedBuilding.value === playerData.value.building || isMovingBuilding.value) return false;
      if (selectedBuilding.value === 2 && !acceptedBuilding2Risk.value) return false;
      return true;
    });

    // คำนวณเวลาที่เหลือจนกว่าจะย้ายได้
    const timeUntilNextMove = computed(() => {
      if (canMoveBuilding.value) return '';
      const now = new Date();
      const nextMoveTime = toDate(playerData.value.nextBuildingMoveTime);
      if (!nextMoveTime) return '';
      const diffMs = nextMoveTime - now;
      if (diffMs <= 0) return '';
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${diffDays} วัน ${diffHours} ชม. ${diffMinutes} นาที`;
    });

    const canUnlockRoom = (index) => {
      if (index >= roomRequirements.length) return false;
      const req = roomRequirements[index];
      return (
        playerData.value.room >= (req.requiredRoom || 0) &&
        playerData.value.level >= (req.requiredLevel || 0) &&
        playerData.value.renown >= (req.requiredRenown || 0) &&
        playerData.value.silver >= (req.silver || 0) &&
        playerData.value.gold >= (req.gold || 0) &&
        playerData.value.diamond >= (req.diamond || 0)
      );
    };

    const getCardClass = (index) => {
      const baseClass = 'relative group rounded-lg transition-all duration-300';
      if (playerData.value.room > index) {
        return `${baseClass} cursor-not-allowed opacity-75`;
      }
      return `${baseClass} cursor-pointer hover:bg-gray-600`;
    };

    const checkInsufficientRequirements = (index) => {
      const req = roomRequirements[index];
      const d = [];
      if (playerData.value.room < (req.requiredRoom || 0)) d.push(`ขาดห้อง ${req.requiredRoom - playerData.value.room} ห้อง`);
      if (playerData.value.level < (req.requiredLevel || 0)) d.push(`ขาดเลเวล ${req.requiredLevel - playerData.value.level} เลเวล`);
      if (playerData.value.renown < (req.requiredRenown || 0)) d.push(`ขาดชื่อเสียง ${req.requiredRenown - playerData.value.renown} หน่วย`);
      if (playerData.value.silver < (req.silver || 0)) d.push(`ขาดเหรียญ ${req.silver - playerData.value.silver} เหรียญ`);
      if (playerData.value.gold < (req.gold || 0)) d.push(`ขาดทอง ${req.gold - playerData.value.gold} ทอง`);
      if (playerData.value.diamond < (req.diamond || 0)) d.push(`ขาดเพชร ${req.diamond - playerData.value.diamond} เพชร`);
      insufficientRequirements.value = d;
    };

    const handleImageError = (e) => {
      e.target.src = 'https://via.placeholder.com/80?text=Image+Not+Found';
    };

    // Helper: แปลง Firestore Timestamp / Date / string เป็น Date object
    const toDate = (value) => {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value.toDate === 'function') return value.toDate();
      if (typeof value === 'number') return new Date(value);
      if (typeof value === 'string') return new Date(value);
      return null;
    };

    const setRandomMessage = () => {
      randomMessage.value = Messages[Math.floor(Math.random() * Messages.length)];
    };

    const fetchPlayerData = () => {
      if (!user.value?.uid) {
        errorMessage.value = 'กรุณาล็อกอินก่อน';
        isFetchingData.value = false;
      }
    };

    watch(() => playerStore.characterData, (data) => {
      if (data) {
        playerData.value = {
          room: data?.room || 1,
          level: data?.level || 1,
          renown: data?.renown || 0,
          silver: data?.silver || 0,
          gold: data?.gold || 0,
          diamond: data?.diamond || 0,
          building: data?.building || 1,
        };
      }
    }, { immediate: true });

    watch(() => playerStore.characterPrivateData, (data) => {
      if (data) {
        playerData.value.nextBuildingMoveTime = toDate(data?.nextBuildingMoveTime) || null;
        isFetchingData.value = false;
      }
    }, { immediate: true });

    const openModal = (index) => {
      if (playerData.value.room > index) return;
      selectedRoom.value = index;
      checkInsufficientRequirements(index);
      showModal.value = true;
    };

    const closeModal = () => {
      showModal.value = false;
      selectedRoom.value = null;
      errorMessage.value = '';
      insufficientRequirements.value = [];
    };

    const openMoveBuildingModal = () => {
      if (!canMoveBuilding.value) {
        errorMessage.value = `คุณสามารถย้ายตึกได้อีกครั้งในอีก ${timeUntilNextMove.value}`;
        return;
      }
      if (!canMoveBuildingByRoom.value) {
        errorMessage.value = 'ต้องมีห้องครบ 12 ห้องเท่านั้นจึงจะย้ายตึกได้';
        return;
      }
      moveBuildingModalOpen.value = true;
      selectedBuilding.value = null;
      acceptedBuilding2Risk.value = false;
      errorMessage.value = '';
    };

    const closeMoveBuildingModal = () => {
      moveBuildingModalOpen.value = false;
      selectedBuilding.value = null;
      acceptedBuilding2Risk.value = false;
      errorMessage.value = '';
    };

    const selectBuilding = (building) => {
      if (playerData.value.building !== building) {
        selectedBuilding.value = building;
        acceptedBuilding2Risk.value = false;
      }
    };

    const confirmMoveBuilding = async () => {
      if (!canConfirmMoveBuilding.value) return;
      isMovingBuilding.value = true;
      errorMessage.value = '';
      try {
        const characterActions = projectFunctions.httpsCallable('gameActions');
        const result = await characterActions({
          action: 'characterActions',
          subAction: 'moveBuilding2',
          building: selectedBuilding.value,
          cooldownDays: 7
        });
        if (!result.data?.success) throw new Error(result.data?.message || 'การย้ายตึกล้มเหลว');
        playerData.value.nextBuildingMoveTime = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
        moveBuildingModalOpen.value = false;
        selectedBuilding.value = null;
        acceptedBuilding2Risk.value = false;
      } catch (err) {
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการย้ายตึก';
      } finally {
        isMovingBuilding.value = false;
      }
    };

    const confirmUnlock = async () => {
      if (!canUnlockRoom(selectedRoom.value) || isUnlocking.value || playerData.value.room !== selectedRoom.value) return;
      isUnlocking.value = true;
      errorMessage.value = '';
      try {
        const characterActions = projectFunctions.httpsCallable('gameActions');
        const result = await characterActions({ action: 'characterActions', subAction: 'unlockRoom', roomNumber: selectedRoom.value + 1 });
        if (!result.data?.success) throw new Error(result.data?.message || 'การปลดล็อคล้มเหลว');
      } catch (err) {
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการปลดล็อค';
      } finally {
        isUnlocking.value = false;
      }
    };

    // ---- Downgrade Room ----
    const openDowngradeModal = () => {
      if (playerData.value.room <= 1) return;
      showDowngradeModal.value = true;
      downgradeError.value = '';
    };

    const closeDowngradeModal = () => {
      showDowngradeModal.value = false;
      downgradeError.value = '';
    };

    const confirmDowngrade = async () => {
      if (playerData.value.room <= 1 || isDowngrading.value) return;
      isDowngrading.value = true;
      downgradeError.value = '';
      try {
        const characterActions = projectFunctions.httpsCallable('gameActions');
        const result = await characterActions({ action: 'characterActions', subAction: 'downgradeRoom' });
        if (!result.data?.success) throw new Error(result.data?.message || 'การลดห้องล้มเหลว');
        showDowngradeModal.value = false;
      } catch (err) {
        downgradeError.value = err.message || 'เกิดข้อผิดพลาดในการลดห้อง';
      } finally {
        isDowngrading.value = false;
      }
    };

    onMounted(() => {
      setRandomMessage();
      fetchPlayerData();
    });



    return {
      // state
      isLoadingAuth,
      user,
      randomMessage,
      playerData,
      isFetchingData,
      isUnlocking,
      isMovingBuilding,
      errorMessage,
      showModal,
      selectedRoom,
      moveBuildingModalOpen,
      selectedBuilding,
      acceptedBuilding2Risk,
      managerImage,
      insufficientRequirements,
      insuranceOpen,

      // downgrade state
      showDowngradeModal,
      isDowngrading,
      downgradeError,

      // data & computed
      roomRequirements,
      totalRooms,
      unlockedRooms,
      remainingLockedRooms,
      progressPercent,
      canMoveBuilding,
      canMoveBuildingByRoom,
      timeUntilNextMove,
      canConfirmMoveBuilding,

      // methods
      handleImageError,
      openModal,
      closeModal,
      confirmUnlock,
      canUnlockRoom,
      getCardClass,
      openMoveBuildingModal,
      closeMoveBuildingModal,
      selectBuilding,
      confirmMoveBuilding,
      openDowngradeModal,
      closeDowngradeModal,
      confirmDowngrade,
    };
  },
};
</script>

<style scoped>
/* พื้นหลัง */
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1') no-repeat center center fixed,
    linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
}

/* สกอร์บาร์ */
#style-1::-webkit-scrollbar {
  width: 5px;
}
#style-1::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
#style-1::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
#style-1::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* โหมดัล: สกอร์บาร์ภายใน */
.max-h-64 {
  max-height: 16rem;
}
.max-h-64::-webkit-scrollbar {
  width: 5px;
}
.max-h-64::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
.max-h-64::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
.max-h-64::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* แอนิเมชัน */
.animate-spin {
  animation: spin 1s linear infinite;
}
.animate-spin-pulse {
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.animate-pulse {
  animation: pulse 1.5s infinite;
}
@keyframes pulse {
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 0.3;
  }
}

/* ปุ่ม */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
button:not(:disabled):hover {
  transform: translateY(-2px);
}
button:not(:disabled):active {
  transform: scale(0.98);
}
</style>